import { formatNative, formatUsd } from './optionsDashboardUtils';
import { formatLocalizedDate, formatLocalizedNumber, formatLocalizedPercent } from './optionsLocaleFormatting';
import { buildPerformanceReturnInfoModel, buildReturnCardModel } from './optionsPerformanceUtils';
import { getDashboardAssetMeta } from './optionsAssetMetadata';

const EPSILON = 1e-9;

export const DASHBOARD_FINANCIAL_VIEW_CURRENCIES = ['usd', 'btc'];

const normalizeZero = (value) => {
  if (value === null || value === undefined) return value;
  const numeric = Number(value);
  return Math.abs(numeric) < EPSILON ? 0 : numeric;
};

const hasMeaningfulNumber = (value) => value !== null && value !== undefined && Math.abs(Number(value)) >= EPSILON;

const getSignedPrefix = (value) => {
  const numeric = normalizeZero(value);
  if (numeric > 0) return '+';
  if (numeric < 0) return '-';
  return '';
};

const getValueTone = (value) => {
  const numeric = normalizeZero(value);
  if (numeric === null || numeric === undefined) return 'text-white';
  if (numeric > 0) return 'text-[#22C55E]';
  if (numeric < 0) return 'text-[#EF4444]';
  return 'text-white';
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return null;
  const percentValue = normalizeZero(Number(value) * 100);
  if (percentValue === 0) return '0%';
  const absValue = Math.abs(percentValue);
  const digits = absValue >= 10 ? 1 : absValue >= 1 ? 2 : 4;
  return `${getSignedPrefix(percentValue)}${formatLocalizedPercent(absValue, digits)}`;
};

const formatDateLong = (value) => {
  return formatLocalizedDate(value, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatChartLabel = (isoString) => {
  return formatLocalizedDate(isoString, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const convertUsdToBtc = (usdValue, btcPrice) => {
  if (!hasMeaningfulNumber(btcPrice) || usdValue === null || usdValue === undefined) return null;
  return Number(usdValue) / Number(btcPrice);
};

const formatSignedUsd = (value, digits = 2) => {
  if (value === null || value === undefined) return '—';
  const numeric = normalizeZero(value);
  return `${getSignedPrefix(numeric)}${formatUsd(Math.abs(numeric), digits)}`;
};

const formatSignedNative = (value, currency, digits = 4) => {
  if (value === null || value === undefined) return '—';
  const numeric = normalizeZero(value);
  return `${getSignedPrefix(numeric)}${formatNative(Math.abs(numeric), currency, digits)}`;
};

const formatCostUsd = (value, digits = 2) => {
  if (value === null || value === undefined) return '—';
  const numeric = normalizeZero(value);
  if (numeric === 0) return formatUsd(0, digits);
  return `-${formatUsd(Math.abs(numeric), digits)}`;
};

const formatCostNative = (value, currency, digits = 4) => {
  if (value === null || value === undefined) return '—';
  const numeric = normalizeZero(value);
  const formatted = formatNative(Math.abs(numeric), currency, digits);
  if (numeric === 0) return formatted;
  return `-${formatted}`;
};

export const getCurrentBtcPrice = ({ accountData = null, performanceData = null, historyData = null } = {}) => {
  const performanceBtc = (performanceData?.currencies || []).find((item) => item.currency === 'BTC')?.ending_usd_price;
  if (hasMeaningfulNumber(performanceBtc)) return Number(performanceBtc);

  const accountBtc = (accountData?.account_equity?.currencies || []).find((item) => item.currency === 'BTC')?.usd_price;
  if (hasMeaningfulNumber(accountBtc)) return Number(accountBtc);

  const historyBtc = [...(historyData?.items || [])].reverse().find((item) => item.currency === 'BTC' && hasMeaningfulNumber(item.usd_price))?.usd_price;
  return hasMeaningfulNumber(historyBtc) ? Number(historyBtc) : null;
};

const getRelevantAccountAssets = (accountData) => {
  return [...(accountData?.account_equity?.currencies || [])]
    .filter((item) => hasMeaningfulNumber(item?.equity_native) || hasMeaningfulNumber(item?.equity_usd))
    .sort((a, b) => {
      const aUsd = hasMeaningfulNumber(a?.equity_usd) ? Number(a.equity_usd) : -1;
      const bUsd = hasMeaningfulNumber(b?.equity_usd) ? Number(b.equity_usd) : -1;
      if (aUsd !== bUsd) return bUsd - aUsd;
      return String(a?.currency || '').localeCompare(String(b?.currency || ''));
    });
};

const buildAccountValuationNote = ({ accountData, t }) => {
  const status = accountData?.account_equity?.total_account_value_status;
  const unresolved = accountData?.account_equity?.unresolved_currencies || [];
  if (!unresolved.length) return null;
  const joined = unresolved.join(', ');
  if (status === 'partial') {
    return t('dashboard.accountValuePartialNote').replace('{currencies}', joined);
  }
  if (status === 'unavailable') {
    return t('dashboard.accountValueUnavailableNote').replace('{currencies}', joined);
  }
  return null;
};

export const buildDashboardAccountSummaryModel = ({ accountData, selectedCurrency, t, historyData = null, performanceData = null }) => {
  const totalUsd = accountData?.account_equity?.total_account_value_usd;
  const btcPrice = getCurrentBtcPrice({ accountData, performanceData, historyData });
  const totalBtc = convertUsdToBtc(totalUsd, btcPrice);
  const assets = getRelevantAccountAssets(accountData).map((item) => {
    const meta = getDashboardAssetMeta(item.currency);
    return {
      key: meta.code,
      currency: meta.code,
      name: meta.name,
      code: meta.code,
      nativeDisplay: formatNative(item.equity_native, meta.code, meta.digits),
      usdDisplay: item.equity_usd !== null && item.equity_usd !== undefined ? formatUsd(item.equity_usd) : t('dashboard.usdValuationUnavailable'),
      usdToneClass: item.equity_usd !== null && item.equity_usd !== undefined ? 'text-white/78' : 'text-[#FBBF24]',
      nativeValue: item.equity_native,
      usdValue: item.equity_usd,
    };
  });

  const totalValue = selectedCurrency === 'btc' && totalBtc !== null
    ? formatNative(totalBtc, 'BTC', 4)
    : formatUsd(totalUsd);

  const totalSecondary = selectedCurrency === 'btc'
    ? (totalUsd !== null && totalUsd !== undefined ? `≈ ${formatUsd(totalUsd)}` : null)
    : (totalBtc !== null && totalBtc !== undefined ? `≈ ${formatNative(totalBtc, 'BTC', 4)}` : null);

  return {
    totalLabel: t('dashboard.accountValueHeadline'),
    totalValue,
    totalSecondary,
    valuationNote: buildAccountValuationNote({ accountData, t }),
    assetsLabel: t('dashboard.assetsLabel'),
    assets,
    snapshotAt: accountData?.account_equity?.snapshot_at ? formatDateLong(accountData.account_equity.snapshot_at) : '—',
    snapshotLabel: t('dashboard.snapshotLabel'),
  };
};

const buildBreakdownValue = ({ usdValue, selectedCurrency, btcPrice }) => {
  if (selectedCurrency === 'btc' && hasMeaningfulNumber(btcPrice) && usdValue !== null && usdValue !== undefined) {
    const btcValue = convertUsdToBtc(usdValue, btcPrice);
    return {
      primary: formatSignedNative(btcValue, 'BTC', 4),
      secondary: `≈ ${formatSignedUsd(usdValue)}`,
      toneClass: getValueTone(usdValue),
    };
  }

  return {
    primary: formatSignedUsd(usdValue),
    secondary: hasMeaningfulNumber(btcPrice) && usdValue !== null && usdValue !== undefined
      ? `≈ ${formatSignedNative(convertUsdToBtc(usdValue, btcPrice), 'BTC', 4)}`
      : null,
    toneClass: getValueTone(usdValue),
  };
};

const hasTradingActivity = (bucket) => {
  return [
    bucket?.realized_pnl_native,
    bucket?.unrealized_pnl_native,
    bucket?.net_pnl_native,
    bucket?.fees_native,
    bucket?.realized_pnl_usd,
    bucket?.unrealized_pnl_usd,
    bucket?.net_pnl_usd,
    bucket?.fees_usd,
  ].some(hasMeaningfulNumber);
};

const getCapitalEventLabel = (eventType, t) => {
  if (!eventType) return t('dashboard.capitalContextLive');
  if (eventType === 'deposit') return t('performance.depositLabel');
  if (eventType === 'withdrawal') return t('performance.withdrawalLabel');
  if (eventType === 'transfer') return t('performance.transferLabel');
  return eventType;
};

const getDashboardCapitalContext = ({ currentCapitalPeriodsData, selectedCurrency, t }) => {
  const views = (currentCapitalPeriodsData?.currencies || []).filter((item) => item.current_period);
  if (!views.length) return null;

  const chosen = selectedCurrency === 'btc'
    ? views.find((item) => item.currency === 'BTC') || views[0]
    : [...views].sort((a, b) => Number(b.current_period?.started_timestamp_ms || 0) - Number(a.current_period?.started_timestamp_ms || 0))[0];

  if (!chosen?.current_period) return null;

  const currentPeriod = chosen.current_period;
  return {
    contextCurrency: chosen.currency,
    sinceLabel: t('dashboard.sinceLastCapitalChange'),
    sinceValue: formatDateLong(currentPeriod.started_at),
    eventLabel: t('dashboard.lastCapitalEvent'),
    eventValue: getCapitalEventLabel(currentPeriod.started_after_cash_flow_type, t),
    showContextCurrency: selectedCurrency === 'usd',
  };
};

export const buildDashboardTradingPerformanceModel = ({
  accountData,
  performanceData,
  currentCapitalPeriodsData,
  capitalPeriodsData,
  selectedCurrency,
  t,
  historyData = null,
  selectedRange = null,
  customRange = null,
}) => {
  const btcPrice = getCurrentBtcPrice({ accountData, performanceData, historyData });
  const headlineBreakdown = buildBreakdownValue({
    usdValue: performanceData?.totals_usd?.net_pnl_usd,
    selectedCurrency,
    btcPrice,
  });

  // Cálculo do Trading P&L Líquido (Realized - Fees)
  const realizedUsd = performanceData?.totals_usd?.realized_pnl_usd || 0;
  const feesUsd = performanceData?.totals_usd?.fees_usd || 0;
  const tradingPnlUsd = realizedUsd - feesUsd;
  const tradingPnlBtc = convertUsdToBtc(tradingPnlUsd, btcPrice);

  const tradingPnlHeadline = selectedCurrency === 'btc' && tradingPnlBtc !== null
    ? formatSignedNative(tradingPnlBtc, 'BTC', 4)
    : formatSignedUsd(tradingPnlUsd);

  const tradingPnlHeadlineSecondary = selectedCurrency === 'btc'
    ? `≈ ${formatSignedUsd(tradingPnlUsd)}`
    : (tradingPnlBtc !== null ? `≈ ${formatSignedNative(tradingPnlBtc, 'BTC', 4)}` : null);

  const tradingPnlToneClass = getValueTone(tradingPnlUsd);

  // 1. Determinar o Início Operacional de Options com base no Capital Periods V2
  let earliestExposureMs = null;
  const datasToSearch = [capitalPeriodsData, currentCapitalPeriodsData].filter(Boolean);
  datasToSearch.forEach((periodsObj) => {
    (periodsObj?.currencies || []).forEach((curr) => {
      const currPeriod = curr.current_period;
      const allPeriodsInCurrency = [...(curr.periods || [])];
      if (currPeriod && !allPeriodsInCurrency.some(p => p.id === currPeriod.id)) {
        allPeriodsInCurrency.push(currPeriod);
      }

      allPeriodsInCurrency.forEach((p) => {
        const isV2Legit = p.v2_activation_type !== null && p.v2_activation_type !== undefined;
        if (isV2Legit) {
          const ms = new Date(p.started_at).getTime();
          if (earliestExposureMs === null || ms < earliestExposureMs) {
            earliestExposureMs = ms;
          }
        }
      });
    });
  });

  // Somar starting_equity_usd de todos os períodos V2 que começaram nesse início operacional (limiar de 24h para multi-asset)
  let initialEquityUsd = 0;
  let hasOperationalStart = false;
  const countedPeriodIds = new Set();

  if (earliestExposureMs !== null) {
    datasToSearch.forEach((periodsObj) => {
      (periodsObj?.currencies || []).forEach((curr) => {
        const currPeriod = curr.current_period;
        const allPeriodsInCurrency = [...(curr.periods || [])];
        if (currPeriod && !allPeriodsInCurrency.some(p => p.id === currPeriod.id)) {
          allPeriodsInCurrency.push(currPeriod);
        }
        allPeriodsInCurrency.forEach((p) => {
          const isV2Legit = p.v2_activation_type !== null && p.v2_activation_type !== undefined;
          if (isV2Legit && !countedPeriodIds.has(p.id)) {
            const ms = new Date(p.started_at).getTime();
            if (Math.abs(ms - earliestExposureMs) <= 24 * 60 * 60 * 1000) {
              const eq = p.starting_equity_usd || p.combined_equity?.initial_combined_usd || 0;
              initialEquityUsd += Number(eq);
              countedPeriodIds.add(p.id);
              hasOperationalStart = true;
            }
          }
        });
      });
    });
  }

  // 2. Determinar o rangeStartMs solicitado
  const getRangeStartMs = () => {
    const now = Date.now();
    if (selectedRange === '30d') return now - 30 * 24 * 60 * 60 * 1000;
    if (selectedRange === '90d') return now - 90 * 24 * 60 * 60 * 1000;
    if (selectedRange === 'ytd') return new Date(new Date().getFullYear(), 0, 1).getTime();
    if (selectedRange === 'all') return 0;
    if (selectedRange === 'custom') {
      const s = customRange?.startDate;
      return s ? new Date(s + 'T00:00:00Z').getTime() : 0;
    }
    return 0;
  };
  const rangeStartMs = getRangeStartMs();

  // 3. Aplicar as regras para determinar o capital inicial e effective start
  const backendStartingEquityUsd = capitalPeriodsData?.account_return?.starting_equity_usd ?? null;
  const backendEffectiveStartAt = capitalPeriodsData?.account_return?.effective_start_at ?? null;
  const backendEffectiveStartMs = backendEffectiveStartAt ? new Date(backendEffectiveStartAt).getTime() : 0;

  let validStartingCapitalUsd = backendStartingEquityUsd;
  let validEffectiveStartAt = backendEffectiveStartAt;
  let isOperationalLimited = false;

  if (hasOperationalStart && earliestExposureMs !== null) {
    const isYtdPreOperational = selectedRange === 'ytd' && earliestExposureMs > rangeStartMs;
    const isRangePreOperational = rangeStartMs <= earliestExposureMs;
    const isBackendPreOperational = backendEffectiveStartMs < earliestExposureMs && (selectedRange === 'all' || selectedRange === 'ytd' || rangeStartMs <= earliestExposureMs);

    if (selectedRange === 'all' || isYtdPreOperational || isRangePreOperational || isBackendPreOperational) {
      // Regra: se o range ou a baseline do backend alcançar período anterior ao primeiro trade, limita ao início operacional
      validStartingCapitalUsd = initialEquityUsd;
      validEffectiveStartAt = earliestExposureMs;
      isOperationalLimited = true;
    } else {
      validStartingCapitalUsd = backendStartingEquityUsd;
      validEffectiveStartAt = backendEffectiveStartAt;
    }
  }

  // 4. Calcular a porcentagem do Resultado / Capital Inicial
  let resultOnStartingCapitalPct = null;
  let resultOnStartingCapitalFormatted = '—';
  let resultOnStartingCapitalToneClass = 'text-white';

  if (validStartingCapitalUsd !== null && validStartingCapitalUsd > 0) {
    resultOnStartingCapitalPct = (tradingPnlUsd / validStartingCapitalUsd);
    resultOnStartingCapitalFormatted = formatPercent(resultOnStartingCapitalPct);
    resultOnStartingCapitalToneClass = getValueTone(resultOnStartingCapitalPct);
  }

  const resultsByAsset = [...(performanceData?.currencies || [])]
    .filter(hasTradingActivity)
    .sort((a, b) => Math.abs(Number(b?.net_pnl_usd || 0)) - Math.abs(Number(a?.net_pnl_usd || 0)))
    .map((bucket) => {
      const meta = getDashboardAssetMeta(bucket.currency);
      return {
        key: meta.code,
        currency: meta.code,
        name: meta.name,
        code: meta.code,
        primary: formatSignedNative(bucket.net_pnl_native, meta.code, meta.digits),
        secondary: bucket.net_pnl_usd !== null && bucket.net_pnl_usd !== undefined ? `≈ ${formatSignedUsd(bucket.net_pnl_usd)}` : null,
        toneClass: getValueTone(bucket.net_pnl_usd ?? bucket.net_pnl_native),
      };
    });

  const breakdown = [
    { key: 'realized', label: t('dashboard.realized'), usdValue: performanceData?.totals_usd?.realized_pnl_usd },
    { key: 'unrealized', label: t('dashboard.unrealized'), usdValue: performanceData?.totals_usd?.unrealized_pnl_usd },
    { key: 'fees', label: t('dashboard.fees'), usdValue: performanceData?.totals_usd?.fees_usd },
  ].map((item) => {
    if (item.key === 'fees') {
      if (selectedCurrency === 'btc' && hasMeaningfulNumber(btcPrice) && item.usdValue !== null && item.usdValue !== undefined) {
        const btcValue = convertUsdToBtc(item.usdValue, btcPrice);
        return {
          ...item,
          primary: formatCostNative(btcValue, 'BTC', 4),
          secondary: `≈ ${formatCostUsd(item.usdValue)}`,
          toneClass: 'text-white',
        };
      }

      return {
        ...item,
        primary: formatCostUsd(item.usdValue),
        secondary: hasMeaningfulNumber(btcPrice) && item.usdValue !== null && item.usdValue !== undefined
          ? `≈ ${formatCostNative(convertUsdToBtc(item.usdValue, btcPrice), 'BTC', 4)}`
          : null,
        toneClass: 'text-white',
      };
    }

    return {
      ...item,
      ...buildBreakdownValue({ usdValue: item.usdValue, selectedCurrency, btcPrice }),
    };
  });

  const returnCard = buildReturnCardModel({
    selectedCurrency,
    performanceData,
    currentCapitalPeriodsData,
    capitalPeriodsData,
    t,
    selectedRange,
    customRange,
  });
  const returnInfo = buildPerformanceReturnInfoModel({
    selectedCurrency,
    returnCard,
    t,
  });

  return {
    sectionLabel: t('dashboard.tradingPerformanceLabel'),
    periodLabel: t('common.period'),
    totalLabel: t('dashboard.tradingPnlTotalLabel'),
    returnLabel: t('performance.returnLabel'),
    headline: headlineBreakdown.primary,
    headlineSecondary: headlineBreakdown.secondary,
    headlineToneClass: headlineBreakdown.toneClass,
    
    // Novas métricas padronizadas líquidas e de capital
    tradingPnlHeadline,
    tradingPnlHeadlineSecondary,
    tradingPnlToneClass,
    startingEquityUsd: validStartingCapitalUsd,
    resultOnStartingCapitalFormatted,
    resultOnStartingCapitalToneClass,
    isOperationalLimited,
    earliestExposureMs,

    returnValue: returnCard.status === 'available' ? returnCard.value : null,
    returnSecondary: returnCard.status === 'available' ? returnCard.helper : null,
    returnToneClass: returnCard.status === 'available' ? returnCard.toneClass : 'text-white',
    returnUnavailable: returnCard.status === 'unavailable'
      ? (selectedCurrency === 'usd' ? t('performance.returnUnavailableLabel') : t('performance.returnUnavailable'))
      : null,
    returnHelper: returnCard.status === 'unavailable' ? returnCard.helper : (selectedCurrency === 'usd' ? returnCard.helper : null),
    returnEffectiveStartNote: returnCard.effectiveStartNote || null,
    returnInfo,
    byAssetLabel: t('dashboard.resultByAssetLabel'),
    byAssetRows: resultsByAsset,
    emptyByAssetLabel: t('dashboard.noTradingResultForRange'),
    breakdown,
    capitalContext: getDashboardCapitalContext({ currentCapitalPeriodsData, selectedCurrency, t }),
  };
};

export const getDashboardOpenOperationsCount = ({ accountData }) => {
  return accountData?.trading_pnl?.totals_usd?.open_operations_count || 0;
};

export const buildDashboardHistorySeries = (items, selectedCurrency) => {
  const grouped = new Map();

  items.forEach((item) => {
    const existing = grouped.get(item.snapshot_at) || {
      snapshot_at: item.snapshot_at,
      snapshot_timestamp_ms: item.snapshot_timestamp_ms,
      total_usd: 0,
      btc_price: null,
    };

    existing.total_usd += Number(item.equity_usd || 0);
    if (item.currency === 'BTC' && hasMeaningfulNumber(item.usd_price)) {
      existing.btc_price = Number(item.usd_price);
    }
    grouped.set(item.snapshot_at, existing);
  });

  return Array.from(grouped.values())
    .sort((a, b) => a.snapshot_timestamp_ms - b.snapshot_timestamp_ms)
    .map((item) => {
      const totalBtc = convertUsdToBtc(item.total_usd, item.btc_price);
      return {
        snapshot_at: item.snapshot_at,
        snapshot_timestamp_ms: item.snapshot_timestamp_ms,
        label: formatChartLabel(item.snapshot_at),
        value: selectedCurrency === 'btc' ? totalBtc : Number(item.total_usd || 0),
        valueUsd: Number(item.total_usd || 0),
      };
    })
    .filter((item) => item.value !== null && item.value !== undefined);
};

export const getDashboardChartTitle = (selectedCurrency, t) => {
  return selectedCurrency === 'btc'
    ? t('dashboard.accountEquityTitleBtc')
    : t('dashboard.accountEquityTitle');
};

export const getDashboardChartValueFormatter = (selectedCurrency) => {
  if (selectedCurrency === 'btc') {
    return (value) => formatNative(value, 'BTC', 4);
  }
  return (value) => formatUsd(value, 0);
};
